import { Lang } from "../i18n";
import { Footer } from "./Footer";
import { Nav } from "./Nav";
import { Seo } from "./Seo";

type PolicySection = {
  title: string;
  body: string[];
};

const updatedAt: Record<Lang, string> = {
  en: "Last updated: June 15, 2026",
  zh: "生效日期：2026 年 6 月 15 日",
};

const intro: Record<Lang, { label: string; title: string; subtitle: string }> = {
  en: {
    label: "Privacy Policy",
    title: "Privacy Policy",
    subtitle:
      "This policy explains how MytePro Technology Limited handles information for the ClawEase website, demo flows, contact forms, and advertising attribution.",
  },
  zh: {
    label: "隐私政策",
    title: "隐私政策",
    subtitle: "本政策说明 MytePro Technology Limited 如何处理 ClawEase 官网、演示流程、联系表单、广告归因和海外访问场景中的相关信息。",
  },
};

const policySections: Record<Lang, PolicySection[]> = {
  en: [
    {
      title: "1. Introduction and scope",
      body: [
        "ClawEase is a product brand operated by MytePro Technology Limited.",
        "This policy explains how information is collected, used, stored, shared, transferred, protected, and otherwise processed when you visit the ClawEase website, request a demo, submit a contact form, or interact with our marketing campaigns.",
        "This policy applies to the ClawEase website and related lead, demo, and marketing pages. Other affiliated or third-party products and websites may have their own privacy policies, which apply separately when you use those services.",
      ],
    },
    {
      title: "2. Information we may collect",
      body: [
        "The demo request page and contact forms are intended for business users who want to evaluate, purchase, or integrate ClawEase services. Information you provide may include your name, work email, phone number, company or organization name, role, industry, service scenario, demo preferences, message content, and other information you choose to submit through forms, email, chat, or other contact channels.",
        "Information collected automatically may include page URL, referrer, source and campaign parameters, browser type, device information, IP-derived region, cookie identifiers, pixel identifiers, click identifiers, log information, and similar technical data used for security, analytics, product improvement, and advertising measurement.",
        "If you participate in a product demo, please use test data whenever possible. Demo pages are intended to show product capabilities and should not be used to submit real medical records, payment information, insurance details, identity documents, or other sensitive personal information unless the relevant production service and legal basis have been separately confirmed.",
      ],
    },
    {
      title: "3. How we use information",
      body: [
        "Personal information submitted through the demo request page or contact forms is used only to respond to your inquiry, verify service fit, arrange a product demo, discuss purchase or onboarding needs, provide requested follow-up, and keep necessary security, compliance, and business records. We do not use this form-submitted information for unrelated products, unrelated marketing, resale, or independent third-party marketing without your consent.",
        "We may use limited technical and campaign information to measure marketing and advertising performance, attribute lead submissions to campaigns, perform administrative audits, comply with legal obligations, and protect our lawful rights and interests.",
      ],
    },
    {
      title: "4. How we share information",
      body: [
        "We do not sell personal information. We may share necessary information with affiliated entities within the MytePro business group when needed to respond to your request, provide services, support operations, improve products, or perform internal management.",
        "We may share limited information with service providers, contractors, and partners that help us host the website, process inquiries, operate CRM or communication tools, provide analytics, deliver communications, measure advertising performance, or maintain security. We require such parties to use the information only for authorized purposes and to apply appropriate confidentiality and security measures.",
        "We may also disclose information where required by applicable law, regulation, court order, government request, legal process, merger, acquisition, asset transfer, or when reasonably necessary to protect the rights, property, safety, systems, users, employees, or business operations of us or others.",
      ],
    },
    {
      title: "5. Cookies, pixels, and advertising measurement",
      body: [
        "We may use cookies, local storage, Google Ads tags, X Pixel, TikTok Pixel, Meta Pixel, and similar technologies to understand site visits, remember basic website preferences, attribute lead submissions, measure campaign effectiveness, prevent abuse, and improve relevant communications.",
        "For advertising conversion measurement, we may report conversion events and campaign metadata, such as click IDs, source parameters, page visits, and interaction events. We do not send the content of the demo request page or contact forms, such as your name, email address, phone number, company name, or message, to Google Ads, X Pixel, TikTok Pixel, or Meta Pixel as conversion event parameters.",
        "Advertising and analytics partners may process identifiers such as cookies, pixels, click IDs, campaign parameters, device information, page visits, and interaction events according to their own policies. You can manage cookies and certain tracking technologies through your browser or device settings, though blocking them may affect attribution, analytics, or parts of the website experience.",
      ],
    },
    {
      title: "6. Cloud services, demos, and third-party links",
      body: [
        "The ClawEase website may use cloud hosting, communication, analytics, advertising, demo, and form-processing services. When these services are used, information may be processed through secure systems or third-party infrastructure that support the website and related business workflows.",
        "The website may contain links to third-party websites, platforms, social media accounts, or integrations. Those third-party services are not controlled by us, and their own privacy policies apply when you access or use them.",
      ],
    },
    {
      title: "7. Storage, international transfers, and retention",
      body: [
        "Because ClawEase serves international users and may rely on affiliated companies or service providers in different regions, information may be transferred to, stored in, or accessed from jurisdictions outside your location where permitted by applicable law and with appropriate safeguards.",
        "We retain information only for as long as necessary for the purposes described in this policy, including responding to inquiries, maintaining business records, measuring campaigns, resolving disputes, protecting security, and complying with legal obligations, unless a longer retention period is required or permitted by law.",
      ],
    },
    {
      title: "8. Security and sensitive information",
      body: [
        "We use reasonable administrative, technical, and organizational safeguards to protect information against loss, misuse, unauthorized access, disclosure, alteration, or destruction. However, no internet transmission or storage system can be guaranteed to be completely secure.",
        "Certain information, such as health, medical, biometric, identity, financial, or precise location information, may be considered sensitive personal information. Please avoid submitting sensitive information through website forms or demos unless it is necessary, legally permitted, and specifically requested for an agreed service scenario.",
      ],
    },
    {
      title: "9. Your choices and privacy rights",
      body: [
        "Depending on your location and applicable law, you may have the right to request access, correction, deletion, restriction, portability, withdrawal of consent, objection to certain processing, or other rights over your personal information. We may verify your identity before fulfilling a request.",
        "You may also manage cookies through your browser settings or opt out of certain marketing communications according to the instructions provided in those communications. Some non-promotional or transactional communications may still be sent where necessary for requested services or legal reasons.",
      ],
    },
    {
      title: "10. Children, updates, and contact",
      body: [
        "The ClawEase website and services are intended for business users and are not directed to children under 18 or the minimum age required by local law. We do not knowingly collect personal information from children through this website.",
        "We may update this policy from time to time. The latest version will be posted on this page, and material changes may be communicated through appropriate channels where required.",
        "If you have questions about this policy or wish to exercise privacy rights, please contact us at info@clawease.ai.",
      ],
    },
  ],
  zh: [
    {
      title: "一、引言与适用范围",
      body: [
        "ClawEase 是由 MytePro Technology Limited 运营的产品品牌。本政策说明您访问 ClawEase 官网、预约演示、提交联系表单、使用演示流程或与广告活动互动时，我们如何收集、使用、储存、共享、转移、保护和处理相关信息。",
        "本政策适用于 ClawEase 官网及相关线索、演示、营销页面。MytePro 业务体系内其他关联产品、第三方产品、网站、平台或服务可能适用各自独立的隐私政策；当您使用这些服务时，请以对应服务的隐私政策为准。",
      ],
    },
    {
      title: "二、我们可能收集的信息",
      body: [
        "预约演示页面和联系表单主要面向希望评估、购买或接入 ClawEase 服务的商业用户。您主动提供的信息可能包括姓名、工作邮箱、手机号、公司或机构名称、职务、所属行业、业务场景、演示偏好、留言内容，以及您通过表单、邮件、聊天工具或其他联系渠道主动提交的信息。",
        "我们自动获取的信息可能包括页面地址、引荐来源、来源及广告活动参数、浏览器类型、设备信息、基于 IP 的大致地区、Cookie 标识、像素标识、点击标识、日志信息及类似技术数据，用于安全防护、统计分析、产品改进和广告效果衡量。",
        "如您参与产品演示，请尽量使用测试数据。演示页面用于展示产品能力，不应用于提交真实医疗记录、支付信息、保险信息、身份证件信息或其他敏感个人信息，除非相关正式服务场景、处理目的和法律基础已另行确认。",
      ],
    },
    {
      title: "三、我们如何使用信息",
      body: [
        "您通过预约演示页面或联系表单提交的个人信息，仅用于回复咨询、确认服务适配、安排产品演示、沟通购买或接入需求、进行您请求的后续联系，以及保存必要的安全、合规和业务记录。未经您的同意，我们不会将该等表单信息用于无关产品、无关营销、转售或第三方独立营销。",
        "我们可能使用有限的技术信息和广告活动信息，用于衡量营销和广告投放效果、将线索提交归因至对应广告活动、进行管理审计、履行法律义务，并保护我们的合法权益。",
      ],
    },
    {
      title: "四、我们如何共享信息",
      body: [
        "我们不会出售您的个人信息。为回复您的请求、提供服务、支持运营、改进产品或进行内部管理，我们可能在必要范围内与 MytePro 业务体系内的关联主体共享相关信息。",
        "我们可能向帮助我们托管网站、处理咨询、运营 CRM 或沟通工具、提供统计分析、发送沟通、衡量广告效果或维护安全的服务提供商、承包商及合作伙伴共享必要信息。我们会要求其仅为授权目的处理信息，并采取适当的保密和安全措施。",
        "在适用法律法规、法院命令、政府机关要求、法律程序、合并、收购、资产转让，或为保护我们及他人的权利、财产、安全、系统、用户、员工或业务运营所合理必需时，我们也可能依法披露相关信息。",
      ],
    },
    {
      title: "五、Cookie、像素和广告统计",
      body: [
        "我们可能使用 Cookie、本地存储、Google Ads 标签、X Pixel、TikTok Pixel、Meta Pixel 以及类似技术，用于了解网站访问、记住基础网站偏好、归因线索提交、衡量广告活动效果、防止滥用，并优化后续沟通。",
        "在广告转化统计中，我们可能上报转化事件和广告活动元数据，例如点击 ID、来源参数、页面访问和交互事件。我们不会将预约演示页面或联系表单中的姓名、邮箱、手机号、公司名称或留言内容作为转化事件参数发送给 Google Ads、X Pixel、TikTok Pixel 或 Meta Pixel。",
        "广告和分析合作方可能按照其自身政策处理 Cookie、像素、点击 ID、广告活动参数、设备信息、页面访问和交互事件等标识或数据。您可以通过浏览器或设备设置管理 Cookie 和部分跟踪技术；关闭后可能影响广告归因、统计分析或部分网站体验。",
      ],
    },
    {
      title: "六、云服务、演示与第三方链接",
      body: [
        "ClawEase 官网可能使用云托管、沟通、分析、广告、演示和表单处理等服务。使用这些服务时，相关信息可能通过支持网站及业务流程的安全系统或第三方基础设施进行处理。",
        "网站可能包含指向第三方网站、平台、社交媒体账号或集成服务的链接。该等第三方服务并非由我们控制，您访问或使用这些服务时，应适用其各自的隐私政策。",
      ],
    },
    {
      title: "七、保存、跨境传输与留存",
      body: [
        "由于 ClawEase 面向国际用户，并可能依赖不同地区的关联公司或服务提供商，在适用法律允许并采取适当保护措施的前提下，相关信息可能被转移至、存储于或访问自您所在地区以外的司法辖区。",
        "除法律法规另有要求或允许外，我们仅在实现本政策所述目的所需期间内保存相关信息，包括回复咨询、维护业务记录、衡量广告活动、解决争议、保障安全和履行法律义务。",
      ],
    },
    {
      title: "八、信息安全与敏感信息提示",
      body: [
        "我们会采取合理的管理、技术和组织措施，防止信息丢失、不当使用、未经授权访问、披露、篡改或毁损。但请理解，任何互联网传输或存储方式都无法保证绝对安全。",
        "健康医疗、生物识别、身份证件、金融账户、精确位置等信息可能属于敏感个人信息。除非在必要、合法且已明确约定的正式服务场景中，请勿通过官网表单或演示页面提交敏感个人信息。",
      ],
    },
    {
      title: "九、您的选择与权利",
      body: [
        "在适用法律允许的范围内，您可以请求访问、更正、删除、限制处理、撤回同意或行使其他与个人信息相关的权利。为保障信息安全，我们可能会在处理请求前验证您的身份。",
        "您也可以通过浏览器设置管理 Cookie，或按照营销沟通中的说明退订部分推广信息。对于您请求的服务或法律要求所必需的非推广类沟通，我们仍可能依法发送。",
      ],
    },
    {
      title: "十、未成年人、政策更新与联系方式",
      body: [
        "ClawEase 官网及相关服务主要面向企业和商业用户，并非面向未满 18 周岁或当地法律规定最低年龄以下的未成年人。我们不会通过本网站故意收集未成年人个人信息。",
        "我们可能不时更新本隐私政策。最新版本将在本页面发布；如发生重大变更，我们会在法律要求的范围内通过适当方式进行提示。",
        "如您有任何隐私问题或权利请求，请通过 info@clawease.ai 联系我们。中文版本有关本政策的成立、生效、履行、解释及纠纷解决，在适用范围内参照中华人民共和国大陆地区法律及有管辖权法院处理。",
      ],
    },
  ],
};

export function PrivacyPolicyPage({ defaultLang = "en" as Lang }: { defaultLang?: Lang }) {
  const lang = defaultLang;

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-[#100f12] font-sans">
      <Seo lang={lang} page="privacy" />
      <Nav lang={lang} />
      <main className="relative overflow-hidden px-5 pt-32 sm:px-10 lg:pt-36">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[460px]"
          style={{
            background:
              "radial-gradient(circle at 18% 2%, rgba(153,108,255,.18), transparent 30%), radial-gradient(circle at 82% 4%, rgba(233,48,201,.12), transparent 32%), linear-gradient(180deg,#fff 0%,rgba(248,249,250,.96) 72%,#f8f9fa 100%)",
          }}
        />

        <section className="relative mx-auto max-w-[980px] pb-20 lg:pb-28">
          <div className="text-center">
            <div className="inline-flex rounded-full bg-[#f3eefa] px-3 py-1 text-[12px] font-extrabold uppercase tracking-[0.12em] text-[#5223b1]">
              {intro[lang].label}
            </div>
            <h1 className="mx-auto mt-4 max-w-[820px] text-[40px] font-extrabold leading-[1.05] tracking-[-0.04em] text-[#100f12] sm:text-[54px]">
              {intro[lang].title}
            </h1>
            <p className="mx-auto mt-4 max-w-[720px] text-[15px] font-semibold leading-7 text-[#775f78]">{intro[lang].subtitle}</p>
            <p className="mt-4 text-[13px] font-bold text-[#8a768c]">{updatedAt[lang]}</p>
          </div>

          <article className="mx-auto mt-9 rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_76px_rgba(46,22,152,.08)] sm:p-8 lg:p-10">
            <div className="space-y-9">
              {policySections[lang].map((section) => (
                <section key={section.title}>
                  <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{section.title}</h2>
                  <div className="mt-4 space-y-3 text-[15px] font-semibold leading-8 text-[#5f5565]">
                    {section.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        </section>
      </main>
      <div className="relative overflow-hidden bg-[#f8f9fa]">
        <div
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(circle at 24% 6%, rgba(153,108,255,.13), transparent 30%), radial-gradient(circle at 76% 14%, rgba(233,48,201,.10), transparent 28%), linear-gradient(180deg,rgba(248,249,250,.98) 0%,rgba(242,239,249,.92) 100%)",
          }}
        />
        <div className="relative">
          <Footer lang={lang} />
        </div>
      </div>
    </div>
  );
}
